// src/context/AuthContext.tsx
import { createContext, useState, useEffect, ReactNode, useCallback } from "react";
import api from "../api/api";
import { useDispatch } from "react-redux";
import { setUser, clearUser, User } from "../redux/userSlice";

interface AuthContextType {
  user: User | null;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  register: (username: string, password: string) => Promise<void>;
  fetchMe: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextType | undefined>(
  undefined
);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setLocalUser] = useState<User | null>(null);
  const dispatch = useDispatch();

  // 🔐 Funktion stabilisieren mit useCallback
  const fetchMe = useCallback(async () => {
    try {
      const res = await api.get<User>("/user/me", { withCredentials: true });
      console.log("fetchMe response:", res.data);
      // Optional: Default leere Arrays, falls Backend nichts zurückliefert
      res.data.ausleihen = res.data.ausleihen || [];
      res.data.reservierungen = res.data.reservierungen || [];
      setLocalUser(res.data);
      dispatch(setUser(res.data)); // in Redux speichern
    } catch (err) {
      console.error("fetchMe failed:", err);
      setLocalUser(null);
      dispatch(clearUser());
    }
  }, [dispatch]); // dispatch ist die einzige externe Abhängigkeit hier drinnen

  const login = async (username: string, password: string) => {
    const params = new URLSearchParams();
    params.append("username", username);
    params.append("password", password);

    await api.post("/login", params, {
      withCredentials: true,
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
    });
    await fetchMe();
  };

  const logout = async () => {
    try {
      await api.post("/logout", {}, { withCredentials: true });
    } catch (err) {
      console.error("Logout failed:", err);
    } finally {
      setLocalUser(null);
      dispatch(clearUser());
    }
  };

  const register = async (username: string, password: string) => {
    await api.post("/register", { username, password });
    await login(username, password); // automatisch einloggen nach Registrierung
  };

  useEffect(() => {
    fetchMe();
  }, [fetchMe]); // Jetzt meckert React nicht mehr, da fetchMe stabil ist!

  return (
    <AuthContext.Provider value={{ user, login, logout, register, fetchMe }}>
      {children}
    </AuthContext.Provider>
  );
};